export const EVENT_MEDIA_BUCKET = "event-media";
export const MAX_EVENT_COVER_BYTES = 8 * 1024 * 1024;
export const EVENT_COVER_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;
export const EVENT_PAGE_SIZE = 20;

export const EVENT_TYPES = [
  "webinar",
  "training",
  "conference",
  "meetup",
  "community",
  "online",
  "platform",
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

export const EVENT_LOCATION_TYPES = [
  "online",
  "in_person",
  "hybrid",
] as const;

export type EventLocationType = (typeof EVENT_LOCATION_TYPES)[number];

export const EVENT_STATUSES = [
  "draft",
  "published",
  "cancelled",
  "completed",
  "archived",
] as const;

export type EventStatus = (typeof EVENT_STATUSES)[number];

export type EventAttendanceStatus = "interested" | "attending" | "cancelled";

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  webinar: "Webinár",
  training: "Továbbképzés",
  conference: "Konferencia",
  meetup: "Szakmai találkozó",
  community: "Közösségi esemény",
  online: "Online esemény",
  platform: "PraxisTárs esemény",
};

export const EVENT_LOCATION_LABELS: Record<EventLocationType, string> = {
  online: "Online",
  in_person: "Személyes",
  hybrid: "Hibrid",
};

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  draft: "Piszkozat",
  published: "Publikált",
  cancelled: "Lemondva",
  completed: "Lezárva",
  archived: "Archivált",
};

export const ATTENDANCE_LABELS: Record<EventAttendanceStatus, string> = {
  interested: "Érdekel",
  attending: "Részt veszek",
  cancelled: "Mégsem megyek",
};

export type EventOrganizer = {
  id: string;
  fullName: string;
};

export type EventCommunity = {
  id: string;
  name: string;
  slug: string;
  visibility: "public" | "closed" | "private" | "service" | null;
};

export type EventSummary = {
  id: string;
  title: string;
  slug: string;
  shortDescription: string | null;
  coverPath: string | null;
  coverUrl: string | null;
  eventType: EventType;
  startsAt: string;
  endsAt: string | null;
  timezone: string;
  locationType: EventLocationType;
  physicalLocation: string | null;
  organizer: EventOrganizer | null;
  community: EventCommunity | null;
  maxAttendees: number | null;
  registrationDeadline: string | null;
  attendingCount: number;
  status: EventStatus;
  myAttendance: EventAttendanceStatus | null;
  canManage: boolean;
};

export type EventDetail = EventSummary & {
  description: string | null;
  agenda: string | null;
  joinUrl: string | null;
  publishedAt: string | null;
  cancelledAt: string | null;
  deletedAt: string | null;
};

export type EventAttendee = {
  userId: string;
  fullName: string;
  status: EventAttendanceStatus;
  updatedAt: string;
};

export type EventCreateInput = {
  title: string;
  shortDescription: string;
  description: string;
  agenda: string;
  coverPath: string | null;
  eventType: EventType;
  startsAt: string;
  endsAt: string | null;
  locationType: EventLocationType;
  physicalLocation: string;
  onlineUrl: string;
  communityId: string | null;
  maxAttendees: number | null;
  registrationDeadline: string | null;
  publish: boolean;
};

export type EventUpdateInput = Omit<EventCreateInput, "publish" | "communityId"> & {
  eventId: string;
};

export type EventTimeWindow = "all" | "today" | "week" | "month";

export type EventListFilters = {
  search: string;
  eventType: EventType | "all";
  locationType: EventLocationType | "all";
  communityId: string | "all";
  timeWindow: EventTimeWindow;
};

export const EMPTY_EVENT_FILTERS: EventListFilters = {
  search: "",
  eventType: "all",
  locationType: "all",
  communityId: "all",
  timeWindow: "all",
};

export function isEventType(value: string): value is EventType {
  return EVENT_TYPES.includes(value as EventType);
}

export function isLocationType(value: string): value is EventLocationType {
  return EVENT_LOCATION_TYPES.includes(value as EventLocationType);
}

export function isEventStatus(value: string): value is EventStatus {
  return EVENT_STATUSES.includes(value as EventStatus);
}

export function isAttendanceStatus(
  value: string,
): value is EventAttendanceStatus {
  return value === "interested" || value === "attending" || value === "cancelled";
}

export function eventHasEnded(event: Pick<EventSummary, "startsAt" | "endsAt">) {
  const end = new Date(event.endsAt ?? event.startsAt).getTime();
  return end < Date.now();
}

export function eventIsFull(event: Pick<EventSummary, "maxAttendees" | "attendingCount">) {
  return (
    event.maxAttendees != null && event.attendingCount >= event.maxAttendees
  );
}

export function registrationClosed(
  event: Pick<EventSummary, "registrationDeadline" | "status">,
) {
  if (event.status !== "published") {
    return true;
  }
  if (!event.registrationDeadline) {
    return false;
  }
  return new Date(event.registrationDeadline).getTime() < Date.now();
}

export function formatEventWhen(iso: string, timezone = "Europe/Budapest") {
  return new Intl.DateTimeFormat("hu-HU", {
    timeZone: timezone,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

export function formatEventDay(iso: string, timezone = "Europe/Budapest") {
  return new Intl.DateTimeFormat("hu-HU", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
  }).format(new Date(iso));
}

export function formatEventTime(iso: string, timezone = "Europe/Budapest") {
  return new Intl.DateTimeFormat("hu-HU", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

export function toDateTimeLocalValue(iso: string) {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
